import React, { useEffect, useState } from 'react';
import SectionHeading from '../SectionHeading';
import ServiceList from '../ServiceList';
import Spacing from '../Spacing';
import Div from '../Div';
import Swal from '../../utils/darkSwal';
import BASE_URL from '../../utils/api';
import { pageTitle } from '../../helper';
import Button from '../Button';
import parse from 'html-react-parser';

export default function ProfilePage() {
  pageTitle('Profile');

  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showUpdateProfile, setShowUpdateProfile] = useState(false);
  const [resetForm, setResetForm] = useState({
    currentPassword: '',
    newPassword: ''
  });

  const [updateForm, setUpdateForm] = useState({
    userName: '',
    phoneNumber: '',
    address: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      Swal.fire({
        icon: 'error',
        title: 'Unauthorized',
        text: 'Please login first.',
        confirmButtonColor: '#1344af',
      });
      return;
    }

    const user = JSON.parse(storedUser);

    try {
      const response = await fetch(`${BASE_URL}/api/users/${user._id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
        setUpdateForm({
          userName: data.userName || '',
          phoneNumber: data.phoneNumber || '',
          address: data.address || ''
        });
        setLoading(false);
      } else {
        const errorData = await response.json();
        Swal.fire({
          icon: 'error',
          title: 'Failed to Load Profile',
          text: errorData.message || 'An error occurred.',
          confirmButtonColor: '#1344af',
        });
        setLoading(false);
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch user profile.',
        confirmButtonColor: '#1344af',
      });
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const token = localStorage.getItem('token');
    const userId = userProfile?._id;

    if (!resetForm.currentPassword || !resetForm.newPassword) {
      return Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Please fill out both password fields.',
        confirmButtonColor: '#1344af',
      });
    }

    try {
      const response = await fetch(`${BASE_URL}/api/users/update-password/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(resetForm),
      });

      const data = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Password Updated',
          text: data.message,
          confirmButtonColor: '#1344af',
        });
        setResetForm({ currentPassword: '', newPassword: '' });
        setShowResetPassword(false);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to Update',
          text: data.message,
          confirmButtonColor: '#1344af',
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Request Failed',
        text: 'Something went wrong.',
        confirmButtonColor: '#1344af',
      });
    }
  };

  const handleUpdateProfile = async () => {
    const token = localStorage.getItem('token');
    const userId = userProfile?._id;

    try {
      const response = await fetch(`${BASE_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateForm),
      });

      const data = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Profile Updated',
          text: data.message || 'Profile has been updated.',
          confirmButtonColor: '#1344af',
        });
        fetchUserProfile(); // refresh
        setShowUpdateProfile(false);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to Update',
          text: data.message,
          confirmButtonColor: '#1344af',
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Request Failed',
        text: 'Something went wrong.',
        confirmButtonColor: '#1344af',
      });
    }
  };

  if (loading) return <p className="text-center">Loading profile...</p>;

  return (
    <>
      <div className="cs-hero cs-style8 cs-shape_wrap_1" id="home">
        <div className="cs-shape_1" />
        <div className="container">
          <div className="cs-hero_img_box">
            <img src="/images/userimg/userimg.png" alt="User" />
          </div>

          <div className="cs-height_0 cs-height_lg_50" />

          <div className="cs-hero_text">
            <h2 className="cs-hero_intro_title">{parse("Hi 👋 I'm")}</h2>
            <h1 className="cs-hero_title">{parse(userProfile?.userName || 'User')}</h1>

            <div className="cs-hero_subtitle cs-font_16">
              <p><strong>Username:</strong> {userProfile?.userName}</p>
              <p><strong>Email:</strong> {userProfile?.email}</p>
              <p><strong>Phone Number:</strong> {userProfile?.phoneNumber}</p>
              <p><strong>Role:</strong> {userProfile?.role?.rolename}</p>
              <p><strong>Address:</strong> {userProfile?.address}</p>
            </div>

            <Spacing lg="100" md="60" />
            <div className="cs-hero_btn">
              <Button
                btnText="Update Profile"
                className="cs-btn cs-style1 ms-2"
                onClick={() => {
                  setShowUpdateProfile(!showUpdateProfile);
                  setShowResetPassword(false);
                }}
              />
              <Button
                btnText="Reset Password"
                className="cs-btn cs-style2 ms-2"
                onClick={() => {
                  setShowResetPassword(!showResetPassword);
                  setShowUpdateProfile(false);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reset Password Form */}
      {showResetPassword && (
        <Div className="container mt-5">
          <Div className="row justify-content-center">
            <Div className="col-sm-12 col-md-6">
              <h4 className="text-center mb-4">Reset Password</h4>
              <input
                type="password"
                className="form-control mb-3"
                placeholder="Current Password"
                value={resetForm.currentPassword}
                onChange={(e) => setResetForm({ ...resetForm, currentPassword: e.target.value })}
              />
              <input
                type="password"
                className="form-control mb-3"
                placeholder="New Password"
                value={resetForm.newPassword}
                onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
              />
              <div className="d-flex justify-content-center">
                <Button btnText="Submit" className="cs-btn cs-style1" onClick={handleResetPassword} />
                <Button btnText="Cancel" className="cs-btn cs-style2 ms-2" onClick={() => {
                  setResetForm({ currentPassword: '', newPassword: '' });
                  setShowResetPassword(false);
                }} />
              </div>
            </Div>
          </Div>
        </Div>
      )}

      {/* Update Profile Form */}
      {showUpdateProfile && (
        <Div className="container mt-5">
          <Div className="row justify-content-center">
            <Div className="col-sm-12 col-md-6">
              <h4 className="text-center mb-4">Update Profile</h4>
              <input
                type="text"
                className="form-control mb-3"
                placeholder="UserName"
                value={updateForm.userName}
                onChange={(e) => setUpdateForm({ ...updateForm, userName: e.target.value })}
              />
              <input
                type="text"
                className="form-control mb-3"
                placeholder="Phone Number"
                value={updateForm.phoneNumber}
                onChange={(e) => setUpdateForm({ ...updateForm, phoneNumber: e.target.value })}
              />
              <input
                type="text"
                className="form-control mb-3"
                placeholder="Address"
                value={updateForm.address}
                onChange={(e) => setUpdateForm({ ...updateForm, address: e.target.value })}
              />
              <div className="d-flex justify-content-center">
                <Button btnText="Update" className="cs-btn cs-style1" onClick={handleUpdateProfile} />
                <Button btnText="Cancel" className="cs-btn cs-style2 ms-2" onClick={() => setShowUpdateProfile(false)} />
              </div>
            </Div>
          </Div>
        </Div>
      )}

      <Spacing lg="100" md="60" />
      <Div className="container">
        <SectionHeading
          title="Profile Actions"
          subtitle="Quick Access"
          variant="cs-style1 text-center"
        />
        <Spacing lg="70" md="45" />
        <ServiceList variant="cs-style2" />
      </Div>
      <Spacing lg="100" md="60" />
    </>
  );
}
